import { defineStore } from 'pinia';
import { computed, ref } from 'vue';
import { uniq, intersection, omit } from 'lodash-es';
import { useI18n } from '@/app/modules/i18n';
import { StoreNames } from '../../../shared/store-names';
import { useUiStatesApi, useUiStateAutoPersist } from '../../ui-states';
import { Announcement, AnnouncementRaw } from '../interfaces';
import { useAnnouncementApi } from '../services/api';

const UI_STATES_KEY = StoreNames.Announcements;

export const useAnnouncementsStore = defineStore(StoreNames.Announcements, () => {
  const i18n = useI18n();
  const api = useUiStatesApi();
  const apiAnnouncement = useAnnouncementApi();

  const viewedAnnouncementsIds = ref<number[]>([]);
  const announcementsRaw = ref<AnnouncementRaw[]>([]);

  const announcements = computed<Announcement[]>(() => {
    return announcementsRaw.value.map((item) => {
      const current = item.translations.find((t) => t.languages_code === i18n.locale.value);
      const fallback = item.translations.find((t) => t.languages_code === 'en-US');

      return {
        ...omit(item, ['translations']),
        title: current?.title || fallback?.title || '',
        text: current?.text || fallback?.text || '',
        excerpt: current?.excerpt || fallback?.excerpt || '',
        actionLabel: current?.actionLabel ?? fallback?.actionLabel ?? null,
        image: item.image ?? null,
        video: item.video ?? null,
      };
    });
  });

  const fetching = ref(false);
  const fetched = ref(false);

  let fetchingPromise: Promise<void> | null = null;

  async function fetch(force = false) {
    if (fetched.value && !force) {
      return fetchingPromise ?? Promise.resolve();
    }

    if (!fetchingPromise) {
      fetchingPromise = (async () => {
        fetching.value = true;

        const persistedState = await api.getUiStates({ key: UI_STATES_KEY }).then(({ data }) => data.data[0]);

        if (persistedState) {
          if (persistedState.value.viewedAnnouncementsIds) {
            viewedAnnouncementsIds.value = persistedState.value.viewedAnnouncementsIds;
          }
        }

        const { data } = await apiAnnouncement.bffGetAnnouncements();

        announcementsRaw.value = data;

        // Remove viewed announcements from the list if they are already not actual

        viewedAnnouncementsIds.value = intersection(
          viewedAnnouncementsIds.value,
          announcementsRaw.value.map(({ id }) => id),
        );

        fetching.value = false;
        fetched.value = true;
        fetchingPromise = null;
      })();
    }

    return fetchingPromise;
  }

  function setAnnouncementViewed(id: number) {
    viewedAnnouncementsIds.value = uniq([...viewedAnnouncementsIds.value, id]);
  }

  function setAllAnnouncementsViewed() {
    viewedAnnouncementsIds.value = uniq([
      ...viewedAnnouncementsIds.value,
      ...announcementsRaw.value.map(({ id }) => id),
    ]);
  }

  const persistingValue = computed(() => ({
    viewedAnnouncementsIds: viewedAnnouncementsIds.value,
  }));

  useUiStateAutoPersist({
    enabled: fetched,
    value: persistingValue,
    key: UI_STATES_KEY,
  });

  return {
    fetching,
    fetched,
    fetch,
    setAnnouncementViewed,
    setAllAnnouncementsViewed,
    announcements,
    viewedAnnouncementsIds,
  };
});
